import { IconType, type TypedMap } from "@common/declarations";
import { Editor, Element, Node, Element as SlateElement, Transforms } from "slate";
import { ReactEditor } from "slate-react";
import {
  type AlignType,
  type BlockType,
  type CustomElement,
  type CustomType,
  type ImageElement,
  type ListType,
  type MarkType,
  type PredefinedTypes,
  type RichEditorToolbarFixedButton,
  type SlateEditor,
  type VideoElement
} from "./types";

export const isMarkActive = (editor, format: MarkType) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const isBlockActive = (editor, format: AlignType | BlockType) => {
  const { selection } = editor;
  if (!selection) return false;

  const blockType = isAlignType(format) ? "align" : "type";

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format
    })
  );

  return !!match;
};

export const isMenuButtonActive = (editor, format: RichEditorToolbarFixedButton) =>
  isCustomType(format) ? false : isMarkType(format) ? isMarkActive(editor, format) : isBlockActive(editor, format);

export const toggleBlock = (editor, format: AlignType | BlockType) => {
  const isActive = isBlockActive(editor, format);
  const isList = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && isListType(n.type) && !isAlignType(format),
    split: true
  });

  let newProperties: Partial<SlateElement>;
  if (isAlignType(format)) {
    newProperties = {
      align: isActive ? undefined : format
    };
  } else {
    newProperties = {
      type: isActive ? "p" : isList ? "list-item" : format
    };
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const toggleMark = (editor, format: MarkType) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const toggleMenuButton = (editor, format: PredefinedTypes) =>
  isMarkType(format) ? toggleMark(editor, format) : toggleBlock(editor, format);

const isAlignType = (format: RichEditorToolbarFixedButton): format is AlignType => {
  switch (format) {
    case "center":
    case "justify":
    case "left":
    case "right":
      return true;
    default:
      return false;
  }
};

const isListType = (format: RichEditorToolbarFixedButton | "list-item"): format is ListType => {
  switch (format) {
    case "ol":
    case "ul":
      return true;
    default:
      return false;
  }
};

const isMarkType = (format: RichEditorToolbarFixedButton): format is MarkType => {
  switch (format) {
    case "bold":
    case "code":
    case "italic":
    case "strikethrough":
    case "underline":
      return true;
    default:
      return false;
  }
};

export const isCustomType = (format: RichEditorToolbarFixedButton): format is CustomType => {
  switch (format) {
    case "ai":
    case "codeview":
    case "forecolor":
    case "fullscreen":
      return true;
    default:
      return false;
  }
};

export type ExpandedButton = {
  icon: IconType;
  tooltip?: string;
};
export const FORMAT_ICONS: Partial<TypedMap<PredefinedTypes, IconType>> = {
  bold: IconType.Bold,
  code: IconType.Code,
  italic: IconType.Italic,
  strikethrough: IconType.Strikethrough,
  underline: IconType.Underline,

  center: IconType.AlignCenter,
  justify: IconType.AlignJustify,
  left: IconType.AlignLeft,
  right: IconType.AlignRight,

  h1: IconType.H1,
  h2: IconType.H2,
  h3: IconType.H3,
  h4: IconType.H4,
  h5: IconType.H5,
  h6: IconType.H6,
  ol: IconType.OrderedList,
  p: IconType.P,
  ul: IconType.UnorderedList,
  a: IconType.Link
};

export const isHeadingNode = (node: Node) => Element.isElement(node) && ["h1", "h2", "h3"].includes(node.type);

export const isMediaNode = (node: Node): node is ImageElement | VideoElement =>
  Element.isElement(node) && ["image", "video"].includes(node.type);

export const insertOrReplaceNode = (editor: SlateEditor, index: number | null, node: CustomElement) => {
  const currentIndex = index ?? editor.selection?.anchor.path[0];
  if (currentIndex === undefined) return;
  const currentNode = editor.children[currentIndex] as CustomElement;
  if (!isMediaNode(currentNode) && Editor.isEmpty(editor, currentNode)) {
    editor.setNodes(node, { at: [currentIndex] });
  } else {
    editor.insertNodeAt(node, currentIndex + 1, true);
  }
};

export const deleteNode = (editor: SlateEditor, node: CustomElement) => {
  editor.delete({ at: ReactEditor.findPath(editor, node) });
  if (editor.children.length === 0) editor.insertNodeAt({ type: "p", children: [{ text: "" }] }, 0);
};

export const appendEmptyLine = (editor: SlateEditor) =>
  editor.insertNodeAt({ type: "p", children: [{ text: "" }] }, editor.children.length, true);
